import { useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
	CardFooter,
} from "@/components/ui/card";
import { useToast } from "@/hooks/useToast";
import { LogIn } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import logo from "@/assets/logo.png";

type LoginForm = {
	email: string;
	password: string;
};

export function Login() {
	const [loading, setLoading] = useState(false);
	const { toast } = useToast();
	const { login } = useAuth();
	const navigate = useNavigate();
	const location = useLocation();
	const {
		register,
		handleSubmit,
		formState: { errors },
	} = useForm<LoginForm>();

	const onSubmit = async (data: LoginForm) => {
		try {
			setLoading(true);
			await login(data.email, data.password);
			toast({
				title: "Success",
				description: "Logged in successfully",
			});
			// Navigate to the page they were trying to access, or home if none
			const from = location.state?.from?.pathname || "/";
			navigate(from);
		} catch (error) {
			console.error("Login submission error:", error);
			toast({
				variant: "destructive",
				title: "Login Failed",
				description:
					error instanceof Error
						? error.message
						: "An error occurred during login. Please try again.",
			});
		} finally {
			setLoading(false);
		}
	};

	return (
		<div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-secondary p-4">
			<Card className="w-full max-w-md">
				<div className="flex justify-center py-6">
					<img src={logo} alt="EpekDigital Logo" className="h-12 w-auto" />
				</div>
				<CardHeader>
					<CardTitle>Welcome back</CardTitle>
					<CardDescription>Enter your credentials to continue</CardDescription>
				</CardHeader>
				<CardContent>
					<form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
						<div className="space-y-2">
							<Label htmlFor="email">Email</Label>
							<Input
								id="email"
								type="email"
								placeholder="Enter your email"
								{...register("email", {
									required: "Email is required",
									pattern: {
										value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
										message: "Invalid email address",
									},
								})}
								className={errors.email ? "border-red-500" : ""}
							/>
							{errors.email && (
								<p className="text-sm text-red-500">{errors.email.message}</p>
							)}
						</div>
						<div className="space-y-2">
							<Label htmlFor="password">Password</Label>
							<Input
								id="password"
								type="password"
								placeholder="Enter your password"
								{...register("password", {
									required: "Password is required",
									minLength: {
										value: 6,
										message: "Password must be at least 6 characters",
									},
								})}
								className={errors.password ? "border-red-500" : ""}
							/>
							{errors.password && (
								<p className="text-sm text-red-500">
									{errors.password.message}
								</p>
							)}
						</div>
						<Button type="submit" className="w-full" disabled={loading}>
							{loading ? (
								"Loading..."
							) : (
								<>
									<LogIn className="mr-2 h-4 w-4" />
									Sign In
								</>
							)}
						</Button>
					</form>
				</CardContent>
				<CardFooter className="flex justify-center">
					<Button
						variant="link"
						className="text-sm text-muted-foreground"
						onClick={() => navigate("/register")}>
						Don't have an account? Sign up
					</Button>
				</CardFooter>
			</Card>
		</div>
	);
}
